import JSZip from 'jszip';
import _ from 'lodash';
import npyjs from 'npyjs';
import * as ort from 'onnxruntime-web';
export default {
  methods: {
    async loadEmbeddingTensors(embeddingData) {
      const jsZip = new JSZip();
      const npzFiles = await jsZip.loadAsync(embeddingData);
      for (const [npy_filename, npy_data] of Object.entries(npzFiles.files)) {
        if (!npy_filename.endsWith('.npy')) {
          continue;
        }

        const npy_array_buffer = await npzFiles.files[npy_filename].async(
          'arraybuffer'
        );
        const bufferType = npy_filename.replace(/\.npy$/, '');
        this.tensorEmbedding[bufferType] = this.parseNpyTensor(
          npy_array_buffer,
          'float32'
        );
      }
    },

    handleMouseOutSegmentation() {
      if (!this.enableSegmentation || !this.enableFastAnnotation) return;
      _.defer(() => {
        this.hoverMaskImage = null;
      });
    },

    getClick(x, y, type) {
      return { x, y, clickType: type };
    },

    isTensorEmbeddingDataExist() {
      for (const key of Object.keys(this.tensorEmbedding)) {
        if (this.tensorEmbedding[key] == null) return false;
      }
      return true;
    },

    performSegmentation: _.throttle(async function (e) {
      try {
        if (!this.isTensorEmbeddingDataExist()) return {};
        const point = this.$refs.layer.getNode().getRelativePointerPosition();
        const x = (point.x / this.stageSize.width) * this.modelSizeOffset;
        const y = (point.y / this.stageSize.height) * this.modelSizeOffset;
        const click = this.getClick(x, y, e.evt.button);
        this.setClicks(click);
        return await this.runOnnx();
      } catch (error) {
        console.error(error);
        return {};
      } finally {
        if (this.isSegmentationHover) this.clicks.pop();
      }
    }, 50),

    setClicks(click) {
      click.clickType = click.clickType === 2 ? 0 : 1;
      this.clicks.push(click);
    },

    parseNpyTensor(tensorBuffer, dType) {
      const npLoader = new npyjs();
      const npArray = npLoader.parse(tensorBuffer);
      const tensor = new ort.Tensor(
        dType,
        this.convertToFloat32(npArray.data),
        npArray.shape
      );
      return tensor;
    },

    convertToFloat32(float16Arr) {
      const out = new Float32Array(float16Arr.length);
      for (let i = 0; i < float16Arr.length; i++) {
        out[i] = float16Arr[i]; // expand precision
      }
      return out;
    },

    imageScale() {
      const LONG_SIDE_LENGTH = 1024;
      let w = this.image.width;
      let h = this.image.height;
      const samScale = LONG_SIDE_LENGTH / Math.max(h, w);
      return { height: h, width: w, samScale };
    },

    /**
     * input: onnx ort.Tensor [B, *, W, H] and index idx
     * output: ort.Tensor [B, idx, W, H]
     **/
    sliceTensor(tensor, idx) {
      const [bs, noMasks, width, height] = tensor.dims;
      const stride = width * height;
      const start = stride * idx;
      const end = start + stride;
      return tensor.cpuData.slice(start, end);
    },

    modelData(clicks, modelScale) {
      let pointCoordsTensor;
      let pointLabelsTensor;

      // Check there are input click prompts
      if (clicks) {
        const flatPoints = this.clicks.map((point) => {
          return [point.x, point.y];
        });

        const flatLabels = this.clicks.map((point) => {
          return point.clickType;
        });

        pointCoordsTensor = new ort.Tensor('float32', flatPoints.flat(), [
          1,
          flatPoints.length,
          2,
        ]);
        pointLabelsTensor = new ort.Tensor('float32', flatLabels, [
          1,
          flatLabels.length,
        ]);
      }
      const imageSizeTensor = new ort.Tensor('int32', [
        modelScale.height,
        modelScale.width,
      ]);

      if (pointCoordsTensor === undefined || pointLabelsTensor === undefined)
        return;

      // There is no previous mask, so default to an empty tensor
      const maskInput =
        this.enableSegmentation && this.previousMaskData.length
          ? new ort.Tensor('float32', this.previousMaskData, [1, 1, 256, 256])
          : new ort.Tensor(
              'float32',
              new Float32Array(256 * 256),
              [1, 1, 256, 256]
            );

      // There is no previous mask, so default to 0
      const hasMaskInput = new ort.Tensor('float32', [
        this.enableSegmentation && this.previousMaskData.length ? 1 : 0,
      ]);

      return {
        image_embed: this.tensorEmbedding.embedding,
        point_coords: pointCoordsTensor,
        point_labels: pointLabelsTensor,
        orig_im_size: imageSizeTensor,
        mask_input: maskInput,
        has_mask_input: hasMaskInput,
        high_res_feats_0: this.tensorEmbedding.high_res_0,
        high_res_feats_1: this.tensorEmbedding.high_res_1,
      };
    },

    arrayToImageData(input, width, height) {
      let [r, g, b, a] = [0, 114, 189, 255]; // the masks's blue color
      if (!this.isSegmentationHover) [r, g, b, a] = [0, 200, 50, 200]; // Green mask, semi-opaque

      const arr = new Uint8ClampedArray(4 * width * height).fill(0);
      for (let i = 0; i < input.length; i++) {
        // Threshold the onnx model mask prediction at 0.0
        // This is equivalent to thresholding the mask using predictor.model.mask_threshold
        // in python
        if (input[i] > 0.0) {
          arr[4 * i + 0] = r;
          arr[4 * i + 1] = g;
          arr[4 * i + 2] = b;
          arr[4 * i + 3] = a;
        }
      }
      return new ImageData(arr, height, width);
    },

    async runOnnx() {
      if (this.model === null) return;
      const feeds = this.modelData(this.clicks, this.imageScale());
      if (!feeds) return;
      if (this.isGeneratingMask) return;
      this.isGeneratingMask = true;
      const results = await this.model.run(feeds);
      const maskTensors = results.masks;
      const [bs, noMasks, width, height] = maskTensors.dims;
      const maskScores = results.iou_predictions.cpuData;
      const bestMaskIdx = maskScores.indexOf(Math.max(...maskScores));
      const bestMaskArray = this.sliceTensor(maskTensors, bestMaskIdx);
      const modelMaskImage = this.onnxMaskToImage(bestMaskArray, width, height);
      if (this.isSegmentationHover) this.hoverMaskImage = modelMaskImage;
      else if (this.enableSegmentation) {
        const bestLowResMasks = this.sliceTensor(
          results.low_res_masks,
          bestMaskIdx
        );

        this.previousMaskData = this.isSegmentationHover
          ? this.previousMaskData
          : bestLowResMasks;
        this.maskImage = modelMaskImage;
      }
      this.isGeneratingMask = false;
      return this.getBoundingBox(bestMaskArray, width, height);
    },

    getBoundingBox(maskArray, height, width) {
      const mask1d = Object.values(maskArray).map(Number);
      if (mask1d.length !== height * width) {
        throw new Error(
          `Expected ${height * width} values, got ${mask1d.length}`
        );
      }
      // Initialize min/max with safe defaults
      let xMin = width,
        xMax = -1;
      let yMin = height,
        yMax = -1;
      // Iterate and update min/max directly
      for (let i = 0; i < mask1d.length; i++) {
        if (mask1d[i] > 0) {
          const y = Math.floor(i / width);
          const x = i % width;
          if (x < xMin) xMin = x;
          if (x > xMax) xMax = x;
          if (y < yMin) yMin = y;
          if (y > yMax) yMax = y;
        }
      }
      if (xMax >= 0 && yMax >= 0) {
        xMin /= width;
        xMax /= width;
        yMin /= height;
        yMax /= height;

        const result = {
          x: Math.min(xMin, xMax),
          y: Math.min(yMin, yMax),
          width: xMax - xMin,
          height: yMax - yMin,
        };
        return result;
      } else {
        const result = {
          x: 0,
          y: 0,
          width: 0,
          height: 0,
        };

        return result;
      }
    },

    onnxMaskToImage(input, width, height) {
      return this.imageDataToImage(this.arrayToImageData(input, width, height));
    },

    imageDataToImage(imageData) {
      const canvas = this.imageDataToCanvas(imageData);
      const image = new Image();
      image.src = canvas.toDataURL();
      return image;
    },

    imageDataToCanvas(imageData) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      ctx?.putImageData(imageData, 0, 0);
      return canvas;
    },
  },
};
